import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "../../../../i18n/LanguageContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCloudUploadAlt,
  faImage,
  faCheckCircle,
  faSpinner,
  faTimesCircle,
  faQuestionCircle,
  faExclamationTriangle,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import toast from "react-hot-toast";
import ImageUploadAPI from "../../services/ImageUploadAPI";
import { useLoading } from "../../../../contexts/LoadingContext";
import { useUser } from "../../context/UserContext";
import "../../css/Common.css";
import "./css/ImageUpload.css";

/**
 * Image upload component with drag-and-drop support
 */
const ImageUpload = ({ onUploadComplete }) => {
  const { t } = useTranslation();
  const { showLoading, hideLoading } = useLoading();
  const { loggedInUser } = useUser();
  const userUuid = loggedInUser?.uuid || loggedInUser?.id;

  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState("");
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [showScreenshotHelp, setShowScreenshotHelp] = useState(false);
  const [screenshotOS, setScreenshotOS] = useState("windows"); // "windows" or "mac"
  const [quotaExceeded, setQuotaExceeded] = useState(false);
  const [uploadSource, setUploadSource] = useState(null); // "paste" or "browse"
  const [context, setContext] = useState("");
  const fileInputRef = useRef(null);

  const ALLOWED_TYPES = [
    "image/jpeg",
    "image/jpg",
    "image/png",
    "image/gif",
    "image/webp",
    "image/bmp",
    "image/tiff",
    "image/svg+xml",
  ];
  const MAX_SIZE = 10 * 1024 * 1024; // 10MB

  /**
   * Detect user's operating system on component mount
   */
  useEffect(() => {
    const userAgent = window.navigator.userAgent.toLowerCase();
    const platform = window.navigator.platform.toLowerCase();

    // Check for Mac/iOS
    if (
      platform.includes("mac") ||
      userAgent.includes("mac") ||
      platform.includes("iphone") ||
      platform.includes("ipad")
    ) {
      setScreenshotOS("mac");
    } else {
      setScreenshotOS("windows");
    }

    // Check quota status
    checkQuotaStatus();
  }, [userUuid]);

  /**
   * Check if quota was previously exceeded today
   */
  const checkQuotaStatus = () => {
    if (!userUuid) return;
    const quotaKey = `image_quota_exceeded_${userUuid}_${new Date().toDateString()}`;
    const exceeded = localStorage.getItem(quotaKey) === "true";
    setQuotaExceeded(exceeded);
  };

  /**
   * Handle paste event for clipboard images (Windows+Shift+S, Ctrl+C, etc.)
   */
  useEffect(() => {
    const handlePaste = (e) => {
      // Check if clipboard contains files
      const items = e.clipboardData?.items;
      if (!items) return;

      // Look for image items
      for (let i = 0; i < items.length; i++) {
        const item = items[i];

        // Check if item is an image
        if (item.type.indexOf("image") !== -1) {
          e.preventDefault(); // Prevent default paste behavior

          const file = item.getAsFile();
          if (file) {
            // Create a more descriptive filename
            const timestamp = new Date().toISOString().replace(/[:.]/g, "-");
            const extension = file.type.split("/")[1] || "png";
            const renamedFile = new File(
              [file],
              `screenshot-${timestamp}.${extension}`,
              { type: file.type },
            );

            handleFileSelect(renamedFile, "paste");
            toast.success(t("imageUpload.toast.pasted"));
          }
          break; // Only handle the first image
        }
      }
    };

    // Add paste event listener
    document.addEventListener("paste", handlePaste);

    // Cleanup
    return () => {
      document.removeEventListener("paste", handlePaste);
    };
  }, []); // Empty dependency array - setup once on mount

  /**
   * Handle file selection
   */
  const handleFileSelect = (file, source = "browse") => {
    setError(null);

    // Validate file type
    if (!ALLOWED_TYPES.includes(file.type)) {
      setError(t("imageUpload.error.invalidType"));
      return;
    }

    // Validate file size
    if (file.size > MAX_SIZE) {
      setError(t("imageUpload.error.tooLarge"));
      return;
    }

    setSelectedFile(file);
    setUploadSource(source);

    // Generate preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  /**
   * Handle file input change
   */
  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0], "browse");
    }
  };

  /**
   * Handle drag events
   */
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  /**
   * Handle drop event
   */
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0], "browse");
    }
  };

  /**
   * Handle upload
   */
  const handleUpload = async () => {
    if (!selectedFile) {
      setError(t("imageUpload.error.selectFile"));
      return;
    }

    setUploading(true);
    setError(null);
    setUploadProgress(0);
    setUploadStatus(t("imageUpload.status.starting"));

    // Show full-screen loading modal
    showLoading(
      "Uploading and analyzing image...",
      "Please wait while we securely upload and analyze your screenshot",
    );

    try {
      const result = await ImageUploadAPI.uploadImage(
        selectedFile,
        (progress, status) => {
          setUploadProgress(progress);
          setUploadStatus(status);
        },
        context,
      );

      // Success
      setUploadStatus(t("imageUpload.status.complete"));

      // Reset form
      setTimeout(() => {
        setSelectedFile(null);
        setPreview(null);
        setUploadProgress(0);
        setUploadStatus("");
        setContext("");
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }

        // Hide loading modal
        hideLoading();

        // Notify parent
        if (onUploadComplete) {
          onUploadComplete(result);
        }
      }, 2000);
    } catch (err) {
      hideLoading();

      // Check if quota exceeded (429 status)
      if (err.response?.status === 429) {
        const errorMessage = err.message || err.response?.data?.error || "";
        if (
          errorMessage.includes("Daily image upload limit exceeded") ||
          errorMessage.includes("quota")
        ) {
          const quotaKey = `image_quota_exceeded_${userUuid}_${new Date().toDateString()}`;
          localStorage.setItem(quotaKey, "true");
          setQuotaExceeded(true);
          setError(t("imageUpload.error.quotaExceeded"));
          setUploadStatus("");
          return;
        }
      }

      setError(err.message || t("imageUpload.error.uploadFailed"));
      setUploadStatus("");
    } finally {
      setUploading(false);
    }
  };

  /**
   * Handle cancel
   */
  const handleCancel = () => {
    setSelectedFile(null);
    setPreview(null);
    setError(null);
    setUploadProgress(0);
    setUploadStatus("");
    setUploadSource(null);
    setContext("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <div className="upload-container">
      <div className="upload-header">
        <h3>
          <FontAwesomeIcon icon={faCloudUploadAlt} /> {t("imageUpload.header")}
        </h3>
      </div>

      {/* Quota Exceeded Banner */}
      {quotaExceeded && (
        <div
          className="quota-exceeded-banner"
          style={{
            backgroundColor: "#fff3cd",
            border: "1px solid #ffc107",
            borderRadius: "4px",
            padding: "12px 16px",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            style={{ color: "#856404", fontSize: "20px" }}
          />
          <div style={{ flex: 1 }}>
            <strong style={{ color: "#856404" }}>
              {t("imageUpload.quota.title")}
            </strong>
            <p
              style={{
                margin: "4px 0 0 0",
                color: "#856404",
                fontSize: "14px",
              }}
            >
              {t("imageUpload.quota.body")}
            </p>
          </div>
        </div>
      )}

      {/* Upload Area */}
      <div className="upload-methods-container">
        {/* Method 1: Paste Screenshot */}
        <div
          className={`upload-dropzone method-paste ${
            selectedFile ? "has-file" : ""
          }`}
        >
          <div className="upload-prompt">
            <FontAwesomeIcon icon={faImage} size="3x" className="upload-icon" />
            <p className="upload-text">
              <strong>{t("imageUpload.paste.title")}</strong>
            </p>
            <p className="upload-hint paste-hint">
              {t("imageUpload.paste.hint")}{" "}
              <kbd>{screenshotOS === "windows" ? "Ctrl" : "Cmd"}</kbd> +{" "}
              <kbd>V</kbd>
              <button
                type="button"
                className="screenshot-help-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowScreenshotHelp(!showScreenshotHelp);
                }}
                title={t("imageUpload.paste.helpTitle")}
              >
                <FontAwesomeIcon icon={faQuestionCircle} />
              </button>
            </p>
            {/* Preview - shown when file is pasted */}
            {selectedFile && preview && uploadSource === "paste" && (
              <div className="upload-preview">
                <img
                  src={preview}
                  alt={t("imageUpload.preview.alt")}
                  className="pasted-preview-image"
                />
              </div>
            )}

            {showScreenshotHelp && (
              <div
                className="screenshot-help"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="screenshot-help-content">
                  <strong>{t("imageUpload.paste.howTo")}</strong>

                  {/* OS Tabs */}
                  <div className="os-tabs">
                    <button
                      className={`os-tab ${
                        screenshotOS === "windows" ? "active" : ""
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setScreenshotOS("windows");
                      }}
                    >
                      {t("imageUpload.paste.winTab")}
                    </button>
                    <button
                      className={`os-tab ${
                        screenshotOS === "mac" ? "active" : ""
                      }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setScreenshotOS("mac");
                      }}
                    >
                      {t("imageUpload.paste.macTab")}
                    </button>
                  </div>

                  <div className="os-instructions">
                    {screenshotOS === "windows" ? (
                      <div className="os-section">
                        <div className="step-by-step">
                          <div className="step">
                            <strong>
                              {t("imageUpload.paste.win.step1label")}
                            </strong>{" "}
                            {t("imageUpload.paste.win.step1press")}{" "}
                            <kbd>Win</kbd> + <kbd>Shift</kbd> + <kbd>S</kbd>
                          </div>
                          <div className="step-detail">
                            {t("imageUpload.paste.win.step1detail")}
                          </div>
                          <div className="step">
                            <strong>
                              {t("imageUpload.paste.win.step2label")}
                            </strong>{" "}
                            {t("imageUpload.paste.win.step2action")}
                          </div>
                          <div className="step-detail">
                            {t("imageUpload.paste.win.step2detail")}
                          </div>
                          <div className="step">
                            <strong>
                              {t("imageUpload.paste.win.step3label")}
                            </strong>{" "}
                            {t("imageUpload.paste.win.step3action")}{" "}
                            <kbd>Ctrl</kbd> + <kbd>V</kbd>
                          </div>
                          <div className="step-detail">
                            {t("imageUpload.paste.win.step3detail")}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="os-section">
                        <div className="step-by-step">
                          <div className="step">
                            <strong>
                              {t("imageUpload.paste.mac.step1label")}
                            </strong>{" "}
                            {t("imageUpload.paste.mac.step1press")}{" "}
                            <kbd>Cmd</kbd> + <kbd>Shift</kbd> + <kbd>4</kbd>
                          </div>
                          <div className="step-detail">
                            {t("imageUpload.paste.mac.step1detail")}
                          </div>
                          <div className="step">
                            <strong>
                              {t("imageUpload.paste.mac.step2label")}
                            </strong>{" "}
                            {t("imageUpload.paste.mac.step2action")}
                          </div>
                          <div className="step-detail">
                            {t("imageUpload.paste.mac.step2detail")}
                          </div>
                          <div className="step">
                            <strong>
                              {t("imageUpload.paste.mac.step3label")}
                            </strong>{" "}
                            {t("imageUpload.paste.mac.step3action")}
                          </div>
                          <div className="step-detail">
                            {t("imageUpload.paste.mac.step3detailA")}{" "}
                            <kbd>Cmd</kbd> + <kbd>Shift</kbd> + <kbd>Ctrl</kbd>{" "}
                            + <kbd>4</kbd>{" "}
                            {t("imageUpload.paste.mac.step3detailB")}
                          </div>
                          <div className="step">
                            <strong>
                              {t("imageUpload.paste.mac.step4label")}
                            </strong>{" "}
                            {t("imageUpload.paste.mac.step4action")}{" "}
                            <kbd>Cmd</kbd> + <kbd>V</kbd>
                          </div>
                          <div className="step-detail">
                            {t("imageUpload.paste.mac.step4detail")}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="upload-methods-divider">
          <span>{t("imageUpload.divider")}</span>
        </div>

        {/* Method 2: File Browser */}
        <div
          className={`upload-dropzone method-file-browser ${
            dragActive ? "drag-active" : ""
          } ${selectedFile ? "has-file" : ""}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => !selectedFile && fileInputRef.current?.click()}
        >
          <div className="upload-prompt">
            <FontAwesomeIcon
              icon={faCloudUploadAlt}
              size="3x"
              className="upload-icon"
            />
            <p className="upload-text">
              <strong>{t("imageUpload.browse.title")}</strong>
            </p>
            <p className="upload-hint">{t("imageUpload.browse.hint1")}</p>
            <p className="upload-hint">{t("imageUpload.browse.hint2")}</p>
            {/* Preview - shown when file is browsed */}
            {selectedFile && preview && uploadSource === "browse" && (
              <div className="upload-preview">
                <img
                  src={preview}
                  alt={t("imageUpload.preview.alt")}
                  className="browsed-preview-image"
                />
              </div>
            )}
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept={ALLOWED_TYPES.join(",")}
          onChange={handleFileInputChange}
          style={{ display: "none" }}
          disabled={uploading}
        />
      </div>

      {/* Error Message */}
      {error && (
        <div className="upload-error">
          <FontAwesomeIcon icon={faTimesCircle} />
          <span>{error}</span>
        </div>
      )}

      {/* Upload Progress */}
      {uploading && (
        <div className="upload-progress">
          <div className="progress-header">
            <FontAwesomeIcon icon={faSpinner} spin />
            <span className="progress-text">{uploadStatus}</span>
          </div>
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: `${uploadProgress}%` }}
            ></div>
          </div>
          <p className="progress-percentage">{uploadProgress.toFixed(0)}%</p>
        </div>
      )}

      {/* Context Field */}
      {selectedFile && !uploading && (
        <div className="form-group" style={{ marginTop: "1rem" }}>
          <label htmlFor="image-context-input">
            <FontAwesomeIcon icon={faInfoCircle} className="label-icon" />
            {t("imageUpload.context.label")}
          </label>
          <textarea
            id="image-context-input"
            value={context}
            onChange={(e) => setContext(e.target.value)}
            placeholder={t("imageUpload.context.placeholder")}
            className="additional-context-textarea"
            rows={3}
          />
        </div>
      )}

      {/* Action Buttons */}
      {selectedFile && !uploading && (
        <div className="upload-actions">
          <button className="analyze-btn" onClick={handleUpload}>
            <FontAwesomeIcon icon={faCloudUploadAlt} />{" "}
            {t("imageUpload.analyzeBtn")}
          </button>
          <button className="clear-btn" onClick={handleCancel}>
            <FontAwesomeIcon icon={faTimesCircle} /> {t("common.cancel")}
          </button>
        </div>
      )}

      {/* Information Panel */}
      <div className="upload-info-panel">
        <h4>
          <FontAwesomeIcon icon={faCheckCircle} />{" "}
          {t("imageUpload.whatNext.title")}
        </h4>
        <ol>
          <li>{t("imageUpload.whatNext.step1")}</li>
          <li>{t("imageUpload.whatNext.step2")}</li>
          <li>{t("imageUpload.whatNext.step3")}</li>
          <li>{t("imageUpload.whatNext.step4")}</li>
        </ol>

        <div className="security-badge">
          <span className="badge-icon">🔒</span>
          <span>{t("imageUpload.security.badge")}</span>
        </div>
      </div>
    </div>
  );
};

export default ImageUpload;
